import React, { useMemo, useState, useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip, useMap, useMapEvents, AttributionControl } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { BRANCHES } from "../lib/data";
import { useChartColors } from "../lib/chartColors";
import { useTheme } from "../lib/theme.jsx";
import { formatPeso, cn } from "../lib/utils";
import { useMapConfig } from "../lib/integrationsApi";
import { branchHealth, HEALTH_META } from "../lib/dataPilot";

// Override default Leaflet icons (we use CircleMarker so this is just safety)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Free fallback basemap (no API key). Premium providers come from /api/integrations/map-config.
const OSM_LAYER = {
  url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};
const FREE_LAYERS = { dark: [{ ...OSM_LAYER, className: "osm-dark-tiles" }], light: [OSM_LAYER] };

function FitBoundsToBranches({ branches }) {
  const map = useMap();
  useEffect(() => {
    if (!branches.length) return;
    const bounds = L.latLngBounds(branches.map(b => [b.lat, b.lng]));
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [branches, map]);
  return null;
}

// Group branches into clusters based on a coarse lat/lng grid sized by zoom level.
function clusterBranches(branches, zoom, invOf) {
  // Cell-size in degrees shrinks as you zoom in: cell = 32 / 2^zoom.
  const cellDeg = 32 / Math.pow(2, zoom);
  const buckets = new Map();
  branches.forEach(b => {
    const key = `${Math.floor(b.lat / cellDeg)}_${Math.floor(b.lng / cellDeg)}`;
    if (!buckets.has(key)) buckets.set(key, { lat: 0, lng: 0, items: [], revenue: 0, inventory: 0, critical: 0 });
    const bucket = buckets.get(key);
    bucket.lat += b.lat;
    bucket.lng += b.lng;
    bucket.items.push(b);
    bucket.revenue += b.monthlyActual;
    bucket.inventory += invOf(b);
    if (b.stockoutRate > 0.06) bucket.critical += 1;
  });
  return Array.from(buckets.values()).map(c => ({ ...c, lat: c.lat / c.items.length, lng: c.lng / c.items.length }));
}

function BranchTip({ b, health, invOf }) {
  if (health) {
    return (
      <div className="text-xs">
        <div className="font-display font-semibold mb-0.5">{b.name}</div>
        <div className="text-[10px] opacity-80 mb-1">{b.region} · {HEALTH_META[branchHealth(b)].label}</div>
        <div className="font-mono-tabular">
          <div>Inventory: {formatPeso(invOf(b), { compact: true })}</div>
          <div>Days on hand: {b.daysOnHand}d</div>
          <div>Stockout rate: {(b.stockoutRate * 100).toFixed(1)}%</div>
        </div>
        <div className="text-[10px] text-primary mt-1">Click to open inventory →</div>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <div className="font-display font-semibold mb-0.5">{b.name}</div>
      <div className="text-[10px] opacity-80 mb-1">{b.brand} · {b.region}</div>
      <div className="font-mono-tabular">
        <div>Rev MTD: {formatPeso(b.monthlyActual, { compact: true })}</div>
        <div>Achievement: {(b.achievement * 100).toFixed(0)}%</div>
        <div>Stockout: {(b.stockoutRate * 100).toFixed(1)}%</div>
      </div>
      <div className="text-[10px] text-primary mt-1">Click to drill down →</div>
    </div>
  );
}

function ClusterLayer({ branches, colorFor, health, invOf, C, onClickBranch, onClickCluster }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });

  const marker = (b, radius) => {
    const color = colorFor(b);
    return (
      <CircleMarker key={b.id} center={[b.lat, b.lng]} radius={radius} className={`branch-marker branch-marker-${b.id}`}
        pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: 1.5 }}
        eventHandlers={{ click: () => onClickBranch(b) }}>
        <LeafletTooltip direction="top" offset={[0, -radius - 2]} opacity={1} className="rotary-tip">
          <BranchTip b={b} health={health} invOf={invOf} />
        </LeafletTooltip>
      </CircleMarker>
    );
  };

  // At zoom >= 9 show individual markers, otherwise cluster.
  if (zoom >= 9) {
    return branches.map(b => {
      const radius = 5 + ((health ? invOf(b) / 3_000_000 : b.monthlyActual / 9_000_000)) * (health ? 1 : 3);
      return (
        <React.Fragment key={b.id}>
          {b.stockoutRate > 0.06 && (
            <CircleMarker center={[b.lat, b.lng]} radius={radius + 4}
              pathOptions={{ color: C.critical, weight: 1, fill: false, opacity: 0.55, className: "branch-pulse" }}
              interactive={false} />
          )}
          {marker(b, radius)}
        </React.Fragment>
      );
    });
  }

  return clusterBranches(branches, zoom, invOf).map((c, i) => {
    if (c.items.length === 1) return marker(c.items[0], 6);
    const n = c.items.length;
    const radius = Math.min(28, 12 + Math.sqrt(n) * 4);
    const hasCritical = c.critical > 0;
    return (
      <React.Fragment key={`cluster-${i}`}>
        {hasCritical && (
          <CircleMarker center={[c.lat, c.lng]} radius={radius + 4}
            pathOptions={{ color: C.critical, weight: 1.2, fill: false, opacity: 0.55, className: "branch-pulse" }}
            interactive={false} />
        )}
        <CircleMarker center={[c.lat, c.lng]} radius={radius}
          pathOptions={{ color: C.primary, fillColor: C.primary, fillOpacity: 0.18, weight: 2, className: "cluster-marker" }}
          eventHandlers={{ click: () => onClickCluster(c) }}>
          <LeafletTooltip direction="top" offset={[0, -radius - 2]} opacity={1} className="rotary-tip" permanent>
            <div className="text-xs px-1 -my-0.5 text-center">
              <div className="font-display font-semibold text-base leading-none">{n}</div>
              <div className="text-[9px] opacity-75 uppercase tracking-widest mt-0.5">branches</div>
              <div className="font-mono-tabular text-[10px] mt-0.5">{formatPeso(health ? c.inventory : c.revenue, { compact: true })}</div>
              {hasCritical && <div className="text-[9px] text-critical mt-0.5 font-semibold">{c.critical} stockout risk</div>}
            </div>
          </LeafletTooltip>
        </CircleMarker>
      </React.Fragment>
    );
  });
}

export default function PhilippinesMap({ filter = "all", onSelect, height = "100%", colorBy = "brand", valueByBranch }) {
  const C = useChartColors();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const mapRef = React.useRef(null);

  const branches = useMemo(() => {
    if (filter === "all") return BRANCHES;
    return BRANCHES.filter(b => b.brand === filter || b.region === filter);
  }, [filter]);

  const brandColor = {
    "1Rotary": C.chart[0],
    "ROTEK": C.chart[1],
    "North ROTEK": C.chart[2],
  };
  const health = colorBy === "health";
  const healthColor = { healthy: C.success, at_risk: C.critical, excess: C.warning };
  const legend = health
    ? Object.entries(HEALTH_META).map(([k, m]) => [m.label, healthColor[k]])
    : Object.entries(brandColor);
  const colorFor = (b) => (health ? healthColor[branchHealth(b)] : brandColor[b.brand]);
  const invOf = (b) => valueByBranch?.[b.id] ?? b.inventoryValue;

  const cfg = useMapConfig();
  const [tileFailed, setTileFailed] = useState(false);
  const tileStats = React.useRef({ ok: 0, err: 0 });
  useEffect(() => { setTileFailed(false); tileStats.current = { ok: 0, err: 0 }; }, [cfg?.provider]);
  const premium = !tileFailed && cfg && cfg.provider !== "osm" && cfg.themes?.[theme];
  const layers = premium ? cfg.themes[theme] : FREE_LAYERS[theme === "dark" ? "dark" : "light"];
  const providerId = premium ? cfg.provider : "osm";
  const providerLabel = premium ? cfg.label : "OpenStreetMap";
  const isFallback = tileFailed || !!cfg?.fallback_reason;
  const tileEvents = {
    tileload: () => { tileStats.current.ok += 1; },
    tileerror: () => {
      tileStats.current.err += 1;
      if (tileStats.current.err >= 4 && tileStats.current.ok === 0) setTileFailed(true);
    },
  };

  function handleClick(branch) {
    if (onSelect) onSelect(branch);
    else navigate(`/inventory?branch=${branch.id}`);
  }

  function handleClusterClick(cluster) {
    if (mapRef.current) {
      mapRef.current.setView([cluster.lat, cluster.lng], Math.min(11, (mapRef.current.getZoom() || 6) + 2));
    }
  }

  return (
    <div className="relative w-full" style={{ height }}>
      <MapContainer
        key={`${theme}-${providerId}`} // re-mount on theme/provider change for tile swap
        ref={mapRef}
        center={[12.5, 122.5]}
        zoom={6}
        minZoom={5}
        maxZoom={11}
        scrollWheelZoom={true}
        zoomControl={true}
        attributionControl={false}
        style={{ width: "100%", height: "100%", background: theme === "dark" ? "#0b0e13" : "#eef2f5" }}
      >
        {layers.map((l, i) => (
          <TileLayer key={i} url={l.url} attribution={l.attribution} tileSize={l.tileSize || 256}
            zoomOffset={l.zoomOffset || 0} className={l.className} maxNativeZoom={l.maxNativeZoom}
            eventHandlers={premium && i === 0 ? tileEvents : undefined} />
        ))}
        <AttributionControl position="bottomright" prefix={false} />
        <FitBoundsToBranches branches={branches} />

        <ClusterLayer
          branches={branches}
          colorFor={colorFor}
          health={health}
          invOf={invOf}
          C={C}
          onClickBranch={handleClick}
          onClickCluster={handleClusterClick}
        />
      </MapContainer>

      {/* Legend overlay */}
      <div data-testid="map-legend" className="absolute top-3 left-12 z-[1000] flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted bg-surface/95 backdrop-blur px-3 py-1.5 rounded-sm border border-border pointer-events-none">
        {legend.map(([name, c]) => (
          <span key={name} className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: c }} />
            {name}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 border-l border-border pl-3">
          <span className="w-2 h-2 rounded-full border border-critical" />
          Stockout risk
        </span>
        <span className="inline-flex items-center gap-1.5 border-l border-border pl-3">
          <span className="w-2 h-2 rounded-full border border-primary bg-primary/20" />
          Cluster · zoom to expand
        </span>
      </div>

      {/* Branch count overlay */}
      <div className="absolute bottom-3 left-12 z-[1000] flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted bg-surface/95 backdrop-blur px-3 py-1.5 rounded-sm border border-border pointer-events-none font-mono-tabular">
        <span className="live-dot" /> {branches.length} branches · live
        <span data-testid="map-provider-chip" className={cn("border-l border-border pl-2", isFallback && "text-warning")}
          title={tileFailed ? "Provider tiles failed to load — using free fallback" : cfg?.fallback_reason || ""}>
          tiles: {providerLabel}{isFallback ? " · fallback" : ""}
        </span>
      </div>
    </div>
  );
}
